import { formatCaptureState } from '../../core/levels/level-display';
import {
  Component,
  booleanAttribute,
  computed,
  effect,
  inject,
  input,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormControl, ValueChangeEvent } from '@angular/forms';
import { MatCheckbox } from '@angular/material/checkbox';
import { TranslatePipe } from '@ngx-translate/core';
import {
  LevelModeToggleComponent,
  LevelModeToggleValue,
} from '../level-mode-toggle/level-mode-toggle.component';
import {
  CaptureState,
  PrimarSub,
  captureStateFromValue,
  captureStateToValue,
  emptyCaptureState,
} from '../../core/levels/level-capture-state';
import {
  QUARTAER_SPECS,
  SEK2_SPECS,
  TERTIAER_SPECS,
  ZYKLEN,
  ZyklusKey,
} from '../../core/levels/level-catalog';
import {
  LevelLanguageSync,
  educationLevelText,
  specText,
  zyklusText,
} from '../../core/levels/level-i18n';
import { EducationLevelEnum, SchoolLevelEnum } from '../../types/rest-api';

type LevelsControl = FormControl<string[] | null> | FormControl<string[]>;
type FlagKey = 'kiga' | 'primar' | 'sek1' | 'sek2' | 'tertiaer' | 'quartaer';
type SpecKey = 'sek2Specs' | 'tertiaerSpecs' | 'quartaerSpecs';

/** Unticking a group also drops the chips it owns. */
const CLEAR_ON_UNTICK: Record<FlagKey, Partial<CaptureState>> = {
  kiga: {},
  primar: { primarSub: [] },
  sek1: {},
  sek2: { sek2Specs: [] },
  tertiaer: { tertiaerSpecs: [] },
  quartaer: { quartaerSpecs: [] },
};

/** The six Stufen, in grid order (row-major: Kindergarten | Primarstufe, …). */
const ROWS: { key: FlagKey; level: EducationLevelEnum; testId: string }[] = [
  { key: 'kiga', level: 'KINDERGARTEN', testId: 'capture-kindergarten' },
  { key: 'primar', level: 'PRIMARSTUFE', testId: 'capture-primarstufe' },
  { key: 'sek1', level: 'SEKUNDARSTUFE_I', testId: 'capture-sek1' },
  { key: 'sek2', level: 'SEKUNDARSTUFE_II', testId: 'capture-sek2' },
  { key: 'tertiaer', level: 'TERTIAERSTUFE', testId: 'capture-tertiaer' },
  { key: 'quartaer', level: 'QUARTAERSTUFE', testId: 'capture-quartaer' },
];

/** Sub-choices shown under a ticked Stufe (primar: Unterstufe/Mittelstufe). */
type SubChoices =
  | { key: 'primar' }
  | { key: SpecKey; specs: readonly SchoolLevelEnum[] };

/**
 * «Schule / Fachkraft» level capture (prototype «Berufsbezeichnung Flow»):
 * Stufen or Zyklen as a two-column checkbox grid; sub-levels / specs of
 * ticked Stufen as compact chip rows below. Writes label arrays straight into the host's
 * existing `levels` / `levelDescriptions` controls, so hosts keep their
 * validators and save mapping.
 *
 * Sync back from the host relies on the control's `events` stream, so a
 * `setValue`/`patchValue` with `emitEvent: false` is not picked up until the
 * next emitting change.
 */
@Component({
  selector: 'app-level-capture',
  standalone: true,
  imports: [MatCheckbox, LevelModeToggleComponent, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './level-capture.component.html',
})
export class LevelCaptureComponent {
  levelsControl = input.required<LevelsControl>();
  /** Optional: hosts that cannot persist specs (subjects, job functions, alerts)
   *  omit it — the spec chips are then not offered at all. */
  descriptionsControl = input<LevelsControl | null>(null);
  hint = input<string | null>(null);
  /** Shows «(falls relevant)» next to the tabs (job functions). */
  optional = input(false, { transform: booleanAttribute });

  state = signal<CaptureState>(emptyCaptureState);
  /** Bumped on every control event so `showError` re-evaluates on touch/status changes. */
  private revision = signal(0);
  private writing = false;

  readonly SEK2 = SEK2_SPECS;
  readonly TER = TERTIAER_SPECS;
  readonly QUA = QUARTAER_SPECS;
  readonly ZYKLEN = ZYKLEN;
  readonly SUBS: { key: PrimarSub; level: EducationLevelEnum }[] = [
    { key: 'unterstufe', level: 'UNTERSTUFE' },
    { key: 'mittelstufe', level: 'MITTELSTUFE' },
  ];
  readonly levelText = educationLevelText;
  readonly specText = specText;
  readonly zyklusText = zyklusText;
  readonly ROWS = ROWS;

  /** Sub-choices per ticked Stufe, rendered right under its checkbox. */
  subChoices = computed<Partial<Record<FlagKey, SubChoices>>>(() => {
    const s = this.state();
    const specs = !!this.descriptionsControl();
    const out: Partial<Record<FlagKey, SubChoices>> = {};
    if (s.primar) out.primar = { key: 'primar' };
    if (specs && s.sek2) out.sek2 = { key: 'sek2Specs', specs: this.SEK2 };
    if (specs && s.tertiaer)
      out.tertiaer = { key: 'tertiaerSpecs', specs: this.TER };
    if (specs && s.quartaer)
      out.quartaer = { key: 'quartaerSpecs', specs: this.QUA };
    return out;
  });

  /** Also mirrored as `.ng-invalid.ng-touched` on the card so
   *  `revealInvalidFields()` can scroll to this field like any other. */
  /** Prototype «Stufenanzeige im Profil» — same formatter as the cards. */
  preview = computed(() => formatCaptureState(this.state()));

  showError = computed(() => {
    this.revision();
    const c = this.levelsControl();
    return c.invalid && (c.touched || c.dirty);
  });

  constructor() {
    inject(LevelLanguageSync);
    // Re-read whenever the host patches the control (edit forms load async).
    effect(onCleanup => {
      const ctrl = this.levelsControl();
      const descriptions = this.descriptionsControl();
      const sync = () =>
        this.state.set(
          captureStateFromValue({
            levels: ctrl.value ?? [],
            levelDescriptions: descriptions?.value ?? [],
          })
        );
      sync();
      // Watch both controls: a host `patchValue({levels, levelDescriptions})`
      // patches them one after the other, so the specs must re-sync too.
      const onEvent = (e: unknown) => {
        if (e instanceof ValueChangeEvent && !this.writing) sync();
        this.revision.update(r => r + 1);
      };
      const subs = [ctrl.events.subscribe(onEvent)];
      if (descriptions) subs.push(descriptions.events.subscribe(onEvent));
      onCleanup(() => subs.forEach(s => s.unsubscribe()));
    });
  }

  private commit(next: CaptureState) {
    this.state.set(next);
    const v = captureStateToValue(next);
    this.writing = true;
    try {
      const levels = this.levelsControl();
      levels.setValue(v.levels);
      levels.markAsDirty();
      this.descriptionsControl()?.setValue(v.levelDescriptions);
    } finally {
      this.writing = false;
    }
  }

  /** Blur on the card counts as touching the control (required-error timing). */
  onBlur() {
    this.levelsControl().markAsTouched();
  }

  /** Each tab keeps its own ticks while editing; only the active tab is
   *  written to the controls (see `captureStateToValue`). */
  setMode(m: LevelModeToggleValue) {
    this.commit({
      ...this.state(),
      mode: m === 'zyklen' ? 'ZYKLEN' : 'STUFEN',
    });
  }

  setFlag(key: FlagKey, on: boolean) {
    this.commit({
      ...this.state(),
      [key]: on,
      ...(on ? {} : CLEAR_ON_UNTICK[key]),
    });
  }

  toggleSub(k: PrimarSub) {
    const s = this.state();
    this.commit({
      ...s,
      primarSub: s.primarSub.includes(k)
        ? s.primarSub.filter(x => x !== k)
        : [...s.primarSub, k],
    });
  }

  toggleSpec(key: SpecKey, v: SchoolLevelEnum) {
    const s = this.state();
    const arr = s[key];
    this.commit({
      ...s,
      [key]: arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v],
    });
  }

  toggleZyklus(k: ZyklusKey) {
    const s = this.state();
    this.commit({
      ...s,
      zyklen: s.zyklen.includes(k)
        ? s.zyklen.filter(z => z !== k)
        : [...s.zyklen, k],
    });
  }

  has<T>(arr: T[], v: T): boolean {
    return arr.includes(v);
  }
}
